import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const FriedRice: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Perfect Chinese Fried Rice Recipe | Chinese Food Maps</title>
        <meta name="description" content="Master the art of making authentic Chinese fried rice at home. Learn the secrets to achieving the perfect wok hei and texture in this classic dish." />
        <meta property="og:title" content="Perfect Chinese Fried Rice Recipe | Chinese Food Maps" />
        <meta property="og:description" content="Master the art of making authentic Chinese fried rice at home." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/fried-rice" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/fried-rice" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Perfect Chinese Fried Rice Recipe</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Chinese fried rice (炒饭) is a classic dish that transforms leftover rice into something magical. The key to perfect fried rice is using day-old rice and achieving proper wok hei (breath of the wok).
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Ingredients:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>4 cups day-old cooked rice</li>
              <li>2 eggs, beaten</li>
              <li>1 cup char siu (BBQ pork), diced</li>
              <li>1 cup frozen peas and carrots</li>
              <li>½ cup onion, diced</li>
              <li>3 green onions, chopped</li>
              <li>2 cloves garlic, minced</li>
              <li>3 tbsp vegetable oil</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Seasoning:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>2 tbsp soy sauce</li>
              <li>1 tsp dark soy sauce</li>
              <li>½ tsp sesame oil</li>
              <li>¼ tsp white pepper</li>
              <li>Salt to taste</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Prepare Rice:</strong> Break up any clumps in the cold rice.
              </li>
              <li>
                <strong>Cook Eggs:</strong> Scramble eggs in oil until just set. Remove and set aside.
              </li>
              <li>
                <strong>Stir-fry:</strong> Cook onions and garlic until fragrant. Add peas and carrots.
              </li>
              <li>
                <strong>Add Rice:</strong> Stir in rice, breaking up any clumps. Add char siu.
              </li>
              <li>
                <strong>Season:</strong> Add soy sauces, sesame oil, and white pepper. Mix well.
              </li>
              <li>
                <strong>Finish:</strong> Return eggs to wok, add green onions, and toss everything together.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Always use day-old rice - fresh rice will be too sticky</li>
                <li>Keep the wok very hot throughout cooking</li>
                <li>Don't overcrowd the wok - cook in batches if needed</li>
                <li>Break up rice clumps before cooking</li>
                <li>Add soy sauce around the edges of the wok for better wok hei</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};